import { FC, MouseEvent, useState } from "react"
import { colors } from "../../utils/theme"
import {
  makeStyles,
  TableHead as MuiTableHead,
  TableCell as MuiTableCell,
  TableRow,
  TableSortLabel,
  TableHeadProps as MuiTableHeadProps,
  Table,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  Typography,
} from "@material-ui/core"
import { Order, TechniquesAndInterventionsTableData } from "../../utils/types"
import ChevronDownIcon from "../atoms/ChevronDownIcon"
import { stableSort, getComparator } from "../../utils/helpers"
import TechniquesAndInterventionsMoreButton from "./TechniquesAndInterventionsMoreButton"
import { useTechniquesAndInterventions } from "../../hooks/useTechniquesAndInterventions"
import TablePaginationActions from "@material-ui/core/TablePagination/TablePaginationActions"
import TechniqueAndInterventionsHelpIcon from "./TechniquesAndInterventionsHelpIcon"
import { TechniqueAndInterventionDialog } from "../organisms/TechniqueAndInterventionDialog"
import { QuestionnaireFragment, NudgeFragment } from "../../generated/graphql"
import { useUser } from "../../hooks/useUser"
import AddButton from "./AddButton"
const useStyles = makeStyles((theme) => ({
  table: {
    color: colors.neutral.denim,
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid ${colors.neutral.clay}`,
    backgroundColor: colors.neutral.white,
    marginTop: theme.spacing(3),
  },
  tableHeaderCell: {
    color: colors.neutral.denim,
    fontWeight: 700,
    borderBottom: `1px solid ${colors.neutral.clay}`,
  },
  tableSortLabel: {
    color: `${colors.neutral.denim} !important`,
    fontWeight: 700,
  },
  noData: {
    width: "100%",
    padding: 27,
    textAlign: "center",
    border: `1px solid ${colors.neutral.clay}`,
    borderTop: 0,
    backgroundColor: colors.neutral.white,
  },
  noDataFirstLine: {
    fontWeight: 700,
    marginBottom: 6,
  },
  hyperlink: {
    color: colors.primary.periwinkle,
    textDecoration: "underline",
    cursor: "pointer",
  },
  row: {
    fontWeight: 300,
  },
  questionnaireColumn: {
    display: "flex",
    alignItems: "center",
  },
  questionnaireScore: {
    height: 33,
    width: 33,
    borderRadius: 4,
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  helpIconContainer: {
    display: "flex",
    alignItems: "center",
  },
  helpIcon: {
    marginLeft: theme.spacing(2),
    height: 24,
  },
  labelHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(3),
  },
  subtitle: {
    color: colors.neutral.denim,
    fontWeight: 300,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}))

interface Column {
  id: keyof TechniquesAndInterventionsTableData
  label: string
}

type TableHeadProps = {
  order: Order
  orderBy: string
  onRequestSort: (
    event: MouseEvent<unknown>,
    property: keyof TechniquesAndInterventionsTableData
  ) => void
} & MuiTableHeadProps
const TableHead: FC<TableHeadProps> = ({ order, orderBy, onRequestSort }) => {
  const classes = useStyles()
  const createSortHandler =
    (property: keyof TechniquesAndInterventionsTableData) =>
    (event: MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }
  const columns: Column[] = [
    {
      id: "domain",
      label: "Domain",
    },
    {
      id: "technique",
      label: "Technique Name",
    },
    {
      id: "type",
      label: "Type",
    },
    {
      id: "default_messaging",
      label: "Default Message",
    },
    {
      id: "more",
      label: "",
    },
  ]
  return (
    <MuiTableHead>
      <TableRow>
        {columns.map((column) => (
          <MuiTableCell
            key={column.id}
            sortDirection={orderBy === column.id ? order : false}
            className={classes.tableHeaderCell}
            align={column.label === "" ? "right" : undefined}
            padding={column.label === "" ? "none" : undefined}
          >
            {column.label !== "" ? (
              <TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : "asc"}
                onClick={createSortHandler(column.id)}
                IconComponent={(props) => <ChevronDownIcon {...props} />}
                classes={{
                  root: classes.tableSortLabel,
                  active: classes.tableSortLabel,
                }}
              >
                {column.label}
              </TableSortLabel>
            ) : (
              <TechniqueAndInterventionsHelpIcon />
            )}
          </MuiTableCell>
        ))}
      </TableRow>
    </MuiTableHead>
  )
}

type ItemFragment = NudgeFragment & QuestionnaireFragment
const TechniquesAndInterventionsTable: FC = () => {
  const user = useUser()
  const classes = useStyles()
  const [item, setItem] = useState<ItemFragment | undefined>(undefined)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [openTechniqueDialog, setOpenTechniqueDialog] = useState(false)
  const [showMoreId, setShowMoreId] = useState<number>(-1)
  const [order, setOrder] = useState<Order>("asc")
  const [orderBy, setOrderBy] =
    useState<keyof TechniquesAndInterventionsTableData>("domain")
  const { techniquesAndInterventions } = useTechniquesAndInterventions()

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }
  const isKsana = user?.user?.email?.endsWith("@ksanahealth.com")
  const canDelete = (
    userId: string,
    orgId: number,
    created_by: string,
    ownerOrgId: number
  ): boolean => {
    console.log(userId, created_by, ownerOrgId, orgId)
    return ownerOrgId === orgId || created_by === userId || isKsana
  }
  return (
    <>
      <div className={classes.labelHeader}>
        <Typography variant="h3" component="h1" className={classes.subtitle}>
          Techniques and Interventions
        </Typography>
        <AddButton
          onClick={() => {
            setItem(undefined)
            setOpenTechniqueDialog(true)
          }}
        />
      </div>
      <Table className={classes.table}>
        <TableHead
          order={order}
          orderBy={orderBy}
          onRequestSort={(
            event: React.MouseEvent<unknown>,
            property: keyof TechniquesAndInterventionsTableData
          ) => {
            const isAsc = orderBy === property && order === "asc"
            setOrder(isAsc ? "desc" : "asc")
            setOrderBy(property)
          }}
        />
        {techniquesAndInterventions &&
          techniquesAndInterventions.length > 0 && (
            <TableBody>
              {stableSort(
                rowsPerPage > 0
                  ? techniquesAndInterventions.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                  : techniquesAndInterventions,
                getComparator(order, orderBy)
              ).map((row, index) => {
                const onClick = () => {
                  //navigate

                  const nudge: ItemFragment = {
                    domain: row.domain.data,
                    technique: row.technique.data,
                    default_messaging: row.default_messaging.data,
                    id: row.id.data,
                    active: row.active.data,
                    public: false,
                    created_at: 0,
                    created_by: row.created_by.data,
                    resource_id: row.resource_id.data,
                    name: "",
                    questionnaire_items: [],
                    client_display_name: "",
                    minutes_to_expiration: 1440,
                  }
                  setItem(nudge)

                  setOpenTechniqueDialog(true)
                }
                return (
                  <TableRow key={index}>
                    <TableCell
                      className={classes.hyperlink}
                      onClick={onClick}
                      role="button"
                    >
                      {row.domain.data ? row.domain.data : "-"}
                    </TableCell>
                    <TableCell
                      className={
                        row.domain.data && row.technique.data
                          ? classes.row
                          : undefined
                      }
                      role="button"
                    >
                      {row.technique.data ? row.technique.data : "-"}
                    </TableCell>
                    <TableCell className={classes.row} role="button">
                      {row.type.data ? "Assessment" : "Nudge"}
                    </TableCell>
                    <TableCell className={classes.row} role="button">
                      {row.default_messaging.data}
                    </TableCell>
                    <TableCell
                      onClick={() => setShowMoreId(row.id.data)}
                      className={classes.row}
                    >
                      {showMoreId === row.id.data ? (
                        <TechniquesAndInterventionsMoreButton
                          canOpen={canDelete(
                            user.user.id,
                            user.user.organizations?.[0].id ?? -1,
                            row.created_by.data,
                            row.more.data?.organization_id
                          )}
                          techniqueAndInterventionId={row.id.data}
                        />
                      ) : (
                        <TechniquesAndInterventionsMoreButton
                          canOpen={false}
                          techniqueAndInterventionId={row.id.data}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          )}
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
              colSpan={12}
              count={techniquesAndInterventions.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  "aria-label": "rows per page",
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
      <TechniqueAndInterventionDialog
        item={item}
        open={openTechniqueDialog}
        onClose={() => {
          setOpenTechniqueDialog(false)
        }}
      />
    </>
  )
}

export default TechniquesAndInterventionsTable
